/**
 * Global keyboard-shortcut composable.
 *
 * Two-key chord nav (Linear / GitHub / Notion convention):
 *   g d → /dashboard
 *   g s → /software
 *   g c → /channels
 *
 * Single-key affordances:
 *   /  → focus the page's filter input (any element with data-shortcut="filter")
 *   ?  → open the shortcuts help dialog (consumer wires `onHelp`)
 *
 * Chord protocol: pressing `g` arms a 1.5 s window. The next non-modifier
 * key resolves the chord and clears state; otherwise the window expires
 * and `g` becomes a no-op (matching Linear's behaviour). The chord
 * never fires while the user is typing in an editable element so we
 * don't steal `g d` while someone writes "good" in the filter box.
 *
 * Mount this once from a long-lived layout component (layouts/AppLayout
 * is the natural home — it's mounted for every authenticated route).
 */
import { onBeforeUnmount, onMounted } from "vue";
import { useRouter } from "vue-router";

const CHORD_TIMEOUT_MS = 1500;

interface ChordTarget {
  key: string;
  path: string;
}
const G_CHORDS: ChordTarget[] = [
  { key: "d", path: "/dashboard" },
  { key: "s", path: "/software" },
  { key: "c", path: "/channels" },
];

/** True for elements that "own" keystrokes — typing into them must
 *  bypass the global shortcut handler. Mirrors GitHub's heuristic. */
function _isEditable(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (t.isContentEditable) return true;
  return false;
}

/** True when any non-Shift modifier is held — Cmd-S / Ctrl-K / Alt-T
 *  belong to the browser or OS, not to us. Shift is allowed because
 *  `?` is `Shift+/` on US keyboards. */
function _hasModifier(e: KeyboardEvent): boolean {
  return e.ctrlKey || e.metaKey || e.altKey;
}

export interface KeyboardShortcutOptions {
  /** Called when `?` is pressed (typically: open the help dialog). */
  onHelp: () => void;
}

export function useKeyboardShortcuts(opts: KeyboardShortcutOptions) {
  const router = useRouter();
  let chordArmed = false;
  let chordTimer: number | null = null;

  function _disarmChord() {
    chordArmed = false;
    if (chordTimer !== null) {
      window.clearTimeout(chordTimer);
      chordTimer = null;
    }
  }

  function _armChord() {
    chordArmed = true;
    if (chordTimer !== null) window.clearTimeout(chordTimer);
    chordTimer = window.setTimeout(_disarmChord, CHORD_TIMEOUT_MS);
  }

  function onKeydown(e: KeyboardEvent) {
    if (_isEditable(e.target)) return;
    if (_hasModifier(e)) return;

    // Resolve the chord first if it's armed — `d` after `g` should
    // navigate, never reach the single-key branch below.
    if (chordArmed) {
      const target = G_CHORDS.find((c) => c.key === e.key);
      _disarmChord();
      if (target) {
        e.preventDefault();
        if (router.currentRoute.value.path !== target.path) {
          void router.push(target.path);
        }
      }
      return;
    }

    if (e.key === "g") {
      _armChord();
      return;
    }

    // `?` is Shift+/ on US layouts; e.key resolves it to "?" directly,
    // so the e.shiftKey check is implicit.
    if (e.key === "?") {
      e.preventDefault();
      opts.onHelp();
      return;
    }

    if (e.key === "/") {
      // Find the page's filter input. Any view that wants to receive
      // `/` just slaps `data-shortcut="filter"` on its <input>; the
      // composable doesn't need a per-route registry.
      const el = document.querySelector<HTMLElement>('[data-shortcut="filter"]');
      if (el) {
        e.preventDefault();
        el.focus();
        // selects the existing text on focus so the user can type-replace
        if (el instanceof HTMLInputElement) el.select();
      }
    }
  }

  onMounted(() => {
    window.addEventListener("keydown", onKeydown);
  });
  onBeforeUnmount(() => {
    window.removeEventListener("keydown", onKeydown);
    _disarmChord();
  });
}

/** Static metadata for the help dialog — keep in sync with the
 *  handler above by importing both from one place. Label and
 *  description fields hold i18n keys (resolved in ShortcutsDialog)
 *  so the dialog stays translated. */
export const SHORTCUT_GROUPS = [
  {
    labelKey: "shortcuts.groups.navigate",
    items: [
      { keys: ["g", "d"], descriptionKey: "shortcuts.items.goDashboard" },
      { keys: ["g", "s"], descriptionKey: "shortcuts.items.goSoftware" },
      { keys: ["g", "c"], descriptionKey: "shortcuts.items.goChannels" },
    ],
  },
  {
    labelKey: "shortcuts.groups.onPage",
    items: [
      { keys: ["/"], descriptionKey: "shortcuts.items.focusFilter" },
      { keys: ["?"], descriptionKey: "shortcuts.items.openHelp" },
      { keys: ["Esc"], descriptionKey: "shortcuts.items.closeDialog" },
    ],
  },
] as const;
